import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import CssBaseline from '@mui/material/CssBaseline';
import {
  Dialog,
  DialogActions,
  DialogTitle,
  Button,
  Grid,
  Snackbar
} from '@mui/material';
import _ from 'lodash';
import { FilterReport, HeaderReport, TableReport } from './components';
import useStyles from './styles';
import {
  tabs,
  constructQuery,
  getTabId,
  dumpFilters,
  parseFilters
} from '../../utils/index';
import {
  createReport,
  fetchReports,
  updateReport,
  deleteReport
} from '../../actions/report';
import { getIndicators } from '../../actions/indicators';

function FlexibleReport() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const reportFilters = tabs;
  const [checked, setChecked] = useState(false);
  const [success, setSuccess] = useState(false);
  const [query, setQuery] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [filters, setFilters] = useState(_.cloneDeep(reportFilters));
  const [reportId, setReportId] = useState('');
  const [reportName, setReportName] = useState('');
  const [enableSelect, setEnableSelect] = useState(false);
  const [enableButtons, setEnableButtons] = useState(true);
  const [enableNewButton, setEnabledNewButton] = useState(false);
  const [openReportNameError, setOpenReportNameError] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [updatedIndicators, setUpdatedIndicators] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const timer = useRef();
  const reports = useSelector(state => state.reports.reports);
  const { loaded: indicatorsLoaded, list: indicators } = useSelector(
    state => state.indicators
  );

  if (!updatedIndicators && indicators && indicators.length > 0) {
    reportFilters[4].sections = [
      ...new Set([...reportFilters[4].sections, ...indicators])
    ];
    setUpdatedIndicators(true);
    setFilters(reportFilters);
  }

  useEffect(() => {
    const getInitialData = async () => {
      await dispatch(fetchReports());
      dispatch(getIndicators());
    };
    getInitialData();
  }, [dispatch]);

  const handleRefreshFilters = selectedReportId => {
    if (selectedReportId === '') {
      setIncludeArchived(false);
      setEnableSelect(false);
      setEnableButtons(true);
      setEnabledNewButton(false);
      return;
    }
    // eslint-disable-next-line no-shadow
    const report = reports.filter(report => report.id === selectedReportId)[0];
    const newFilters = parseFilters(tabs, report.reportFilters);
    setIncludeArchived(report.archived);
    setFilters(newFilters);
    const tabId = getTabId(newFilters);
    setSelectedTab(tabId);
    setQuery(constructQuery(newFilters));
  };

  const handleChangeSelect = event => {
    if (event.target.value !== '') {
      setReportId(event.target.value);
      handleRefreshFilters(event.target.value);
      setEnableSelect(false);
      setEnableButtons(false);
      setSuccess(false);
    } else {
      setReportId('');
      setEnableButtons(true);
      setSuccess(false);
      setIncludeArchived(false);
    }
  };

  const handleShowNewName = () => {
    setChecked(prev => !prev);
    if (checked) {
      setEnableSelect(false);
      setEnableButtons(true);
      setEnabledNewButton(false);
      setReportName('');
      setReportId('');
      setEditMode(false);
    }
  };

  const handleNewReport = () => {
    const cleanFilters = _.cloneDeep(filters);
    cleanFilters.forEach(cfilter => {
      if (cfilter.id === 3 || cfilter.id === 4) {
        cleanFilters[cfilter.id].sections.forEach(section => {
          if (section.stages) {
            section.stages.forEach(stage => {
              stage.filters.forEach(filter => {
                // eslint-disable-next-line no-param-reassign
                filter.checked = false;
              });
            });
          } else {
            section.filters.forEach(filter => {
              // eslint-disable-next-line no-param-reassign
              filter.checked = false;
            });
          }
        });
      } else {
        cleanFilters[cfilter.id].filters.forEach(filter => {
          // eslint-disable-next-line no-param-reassign
          filter.checked = false;
        });
      }
    });
    setReportId('');
    setReportName('');
    setFilters(_.cloneDeep(cleanFilters));
    setEnableSelect(true);
    setEnableButtons(true);
    setEnabledNewButton(true);
    handleShowNewName();
    setIncludeArchived(false);
  };

  const handleEditReport = () => {
    const selectedReport = reports.filter(report => report.id === reportId)[0];
    setReportName(selectedReport.name);
    setEnableSelect(true);
    setEnableButtons(true);
    setEnabledNewButton(true);
    setEditMode(true);
    handleShowNewName();
  };

  const handleOpenSnackbar = () => {
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const runQuery = async (filters) => {
    axios.post('v4/flexible/report', { filters });
  };

  const handleRunReport = (fromSaveAndRun, e) => {
    setSuccess(false);
    if (!fromSaveAndRun) {
      e.stopPropagation();
    }
    timer.current = setTimeout(() => {
      setSuccess(true);
      runQuery(filters);
    }, 100);
  };

  const handleOnUpdateFilter = (
    index,
    tabId,
    sectionId,
    stageIndex,
    isChecked
  ) => {
    setSuccess(false);
    const updatedfilters = [...filters];
    if (sectionId && stageIndex !== undefined) {
      updatedfilters[tabId].sections[sectionId].stages[stageIndex].filters[
        index
      ].checked = isChecked;
    } else if (sectionId && stageIndex === undefined) {
      updatedfilters[tabId].sections[sectionId].filters[
        index
      ].checked = isChecked;
    } else {
      updatedfilters[tabId].filters[index].checked = isChecked;
    }
    setFilters(updatedfilters);
    const previewQuery = constructQuery(updatedfilters);
    setQuery(previewQuery);
  };

  const handleClearAllOnSection = (value, sectionId, tabId) => {
    const newFilters = _.cloneDeep(filters);
    if (newFilters[tabId].sections[sectionId].stages) {
      newFilters[tabId].sections[sectionId].stages.forEach(stage => {
        stage.filters.forEach(filter => {
          // eslint-disable-next-line no-param-reassign
          filter.checked = value;
        });
      });
    } else {
      newFilters[tabId].sections[sectionId].filters.forEach(filter => {
        // eslint-disable-next-line no-param-reassign
        filter.checked = value;
      });
    }
    setFilters(newFilters);
  };

  // eslint-disable-next-line consistent-return
  const handleClearAll = (value, tabId, sectionId) => {
    let newFilters = [];
    if (sectionId) {
      return handleClearAllOnSection(value, sectionId, tabId);
    }
    if (tabId === 3 || tabId === 4) {
      newFilters = _.cloneDeep(filters);
      newFilters[tabId].sections.forEach(section => {
        if (section.stages) {
          section.stages.forEach(stage => {
            stage.filters.forEach(filter => {
              // eslint-disable-next-line no-param-reassign
              filter.checked = value;
            });
          });
        } else {
          section.filters.forEach(filter => {
            // eslint-disable-next-line no-param-reassign
            filter.checked = value;
          });
        }
      });
    } else {
      newFilters = _.cloneDeep(filters);
      newFilters[tabId].filters.forEach(filter => {
        // eslint-disable-next-line no-param-reassign
        filter.checked = value;
      });
    }
    setFilters(newFilters);
    setFilters(newFilters);
  };

  const handleSaveReport = (reportTitle, fromSaveAndRun) => {
    if (editMode) {
      dispatch(
        updateReport(
          reportId,
          reportTitle,
          dumpFilters(filters),
          includeArchived
        )
      );
    } else {
      dispatch(
        createReport(reportTitle, dumpFilters(filters), includeArchived)
      );
    }
    if (!fromSaveAndRun) {
      setReportId('');
      setReportName('');
    } else {
      setChecked(false);
      setEnableSelect(false);
      setEnableButtons(false);
      setEnabledNewButton(false);
    }
    setEditMode(false);
  };

  const handleDeleteReport = () => {
    dispatch(deleteReport(reportId));
    setReportId('');
    setReportName('');
    handleShowNewName();
  };

  const handleCloseReportNameError = () => {
    setOpenReportNameError(false);
  };

  const onChangeArchived = () => {
    setIncludeArchived(!includeArchived);
  };

  return (
    <div className={classes.root}>
      <CssBaseline />
      <main className={classes.content}>
        <Grid
          container
          direction="column"
          justify="center"
          alignItems="stretch"
          spacing={2}
        >
          <Grid item>
            <HeaderReport
              handleShowNewName={handleShowNewName}
              handleRunReport={handleRunReport}
              reportsData={reports}
              handleRefreshFilters={handleRefreshFilters}
              handleChangeSelect={handleChangeSelect}
              handleNewReport={handleNewReport}
              handleEditReport={handleEditReport}
              reportId={reportId}
              enableSelect={enableSelect}
              enableButtons={enableButtons}
              enableNewButton={enableNewButton}
              indicatorsLoaded={indicatorsLoaded}
            />
          </Grid>
          <Grid item>
            <FilterReport
              handleShowNewName={handleShowNewName}
              handleOpenSnackbar={handleOpenSnackbar}
              checked={checked}
              handleOnUpdateFilter={handleOnUpdateFilter}
              handleClearAll={handleClearAll}
              reportsData={reports}
              reportName={reportName}
              reportId={reportId}
              handleSaveReport={handleSaveReport}
              handleDeleteReport={handleDeleteReport}
              setOpenReportNameError={setOpenReportNameError}
              editMode={editMode}
              tabs={filters}
              tabId={selectedTab}
              indicatorsLoaded={indicatorsLoaded}
              includeArchived={includeArchived}
              onChangeArchived={onChangeArchived}
              handleRunReport={handleRunReport}
            />
          </Grid>
          <Grid
            item
            style={{
              maxWidth: 'calc(100vw - 50px)'
            }}
          >
            <TableReport
              query={query}
              success={success}
              filters={filters}
              tabId={selectedTab}
              includeArchived={includeArchived}
              indicatorsLoaded={indicatorsLoaded}
            />
          </Grid>
        </Grid>
      </main>
      <div>
        <Snackbar
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          open={openSnackbar}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          ContentProps={{
            'aria-describedby': 'message-id'
          }}
          message={<span id="message-id">Filters saved.</span>}
        />
      </div>
      <div>
        <Dialog
          open={openReportNameError}
          onClose={handleCloseReportNameError}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            <div className={classes.dialogTitle}>
              The report name already exists. Please choose another.
            </div>
          </DialogTitle>
          <DialogActions>
            <Button onClick={handleCloseReportNameError} color="primary">
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}

export default FlexibleReport;
